import type {
  GoldHoldingRow,
  GoldHoldingsLatestResponse,
} from '../api/client'

/**
 * Fund-neutral holdings-hybrid display bridge (GGE per-holding live-hybrid
 * arc PR-C, Task C1). Originally gold-only (gold-evening-hybrid PR C, Task 9)
 * as `deriveGoldHybrid` in `goldHybrid.ts` — widened here to accept either
 * fund's holdings-latest payload, since both
 * GoldHoldingsHybridService and GgeHoldingsHybridService (PR-B, see
 * task-B3-report.md) publish an identical field shape: they differ only in
 * the reference-future timestamp key (`gcNowUtc` vs `clNowUtc`), which this
 * util does not read.
 *
 * /api/gold/holdings-latest is a thin
 * passthroughs of synth_nav:{gold,gge}:holdings_latest, published every 60s
 * during each fund's live session by the respective HoldingsHybridService —
 * per-holding live/proxy hybrid indication for the live-session NAV.
 *
 * deriveHoldingsHybrid is the single source of truth for HoldingsPanel —
 * defensive by design: any missing/non-numeric field degrades gracefully,
 * never throws. `active` requires a non-empty `holdings` array AND a
 * `computedAtUtc` that parses AND is fresher than 15 minutes relative to
 * `nowUtc`.
 *
 * `deriveGoldHybrid` remains exported below as a compatibility alias for one
 * release (grep-friendliness, spec §11.1) — HoldingsPanel.vue/PnlPanel.vue
 * still import it (via the `utils/goldHybrid.ts` re-export shim) until
 * Task C2 switches them to `deriveHoldingsHybrid` directly.
 *
 * Plan deviation (Global Constraints "client re-tick" clause, resolved
 * 2026-09-04): neither synth_nav:gold:enriched nor its GGE analogue expose a
 * live futures/reference price to the client — so there is no client re-tick
 * logic here; each publisher's 60s server cadence stands as the sole
 * refresh mechanism.
 */
const STALE_AFTER_MINUTES = 15

// GOLD only since 2026-11-03: GGE's hybrid was deleted (its Holdings rows read the shadow K_t's legs).
export type HoldingsHybridRow = GoldHoldingRow
export type HoldingsLatestResponse = GoldHoldingsLatestResponse

export interface HoldingsHybridView {
  active: boolean
  aggregateFrac: number | null
  coveredWeightFrac: number | null
  liveWeightFrac: number | null
  // Weight on delayed (EODHD) quotes — GOLD only, 2026-10-15; null when absent.
  delayedWeightFrac: number | null
  confidence: string | null
  rowsByTicker: Map<string, HoldingsHybridRow>
}

// Compatibility alias — pre-PR-C name for the same view shape.
export type GoldHybridView = HoldingsHybridView

const INACTIVE: HoldingsHybridView = {
  active: false,
  aggregateFrac: null,
  coveredWeightFrac: null,
  liveWeightFrac: null,
  delayedWeightFrac: null,
  confidence: null,
  rowsByTicker: new Map(),
}

function asFiniteNumber(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function asNonEmptyString(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

export function deriveHoldingsHybrid(
  resp: HoldingsLatestResponse | null | undefined,
  nowUtc: Date = new Date(),
): HoldingsHybridView {
  if (!resp) return { ...INACTIVE, rowsByTicker: new Map() }

  const holdings = Array.isArray(resp.holdings) ? resp.holdings : []

  const computedAtUtc = asNonEmptyString(resp.computedAtUtc)
  let freshnessOk = false
  if (computedAtUtc) {
    const computedDate = new Date(computedAtUtc)
    if (!Number.isNaN(computedDate.getTime())) {
      const ageMinutes = (nowUtc.getTime() - computedDate.getTime()) / 60_000
      freshnessOk = ageMinutes < STALE_AFTER_MINUTES
    }
  }

  const rowsByTicker = new Map<string, HoldingsHybridRow>()
  for (const row of holdings) {
    const ticker = row && typeof row.ticker === 'string' && row.ticker.length > 0 ? row.ticker : null
    if (!ticker) continue
    rowsByTicker.set(ticker, row)
  }

  const active = holdings.length > 0 && freshnessOk
  if (!active) return { ...INACTIVE, rowsByTicker: new Map() }

  return {
    active,
    aggregateFrac: asFiniteNumber(resp.aggregateIndicationFrac),
    coveredWeightFrac: asFiniteNumber(resp.coveredWeightFrac),
    liveWeightFrac: asFiniteNumber(resp.liveWeightFrac),
    delayedWeightFrac: asFiniteNumber('delayedWeightFrac' in resp ? resp.delayedWeightFrac : undefined),
    confidence: asNonEmptyString(resp.confidence),
    rowsByTicker,
  }
}

// Compatibility alias — deriveGoldHybrid kept for one release
// (grep-friendliness, spec §11.1). New call sites should use
// deriveHoldingsHybrid directly.
export const deriveGoldHybrid = deriveHoldingsHybrid
